import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Slot } from "radix-ui";
import { cn } from "./utils";
import { useSurface, SurfaceProvider } from "./surface-context";
import { surfaceClasses } from "./surface-classes";

interface ElevatedProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Steps above the current substrate.
   *
   * The component's own surface level becomes `min(substrate + offset, 8)`
   * and is re-provided to descendants via SurfaceProvider, so further
   * nesting walks up the ladder automatically.
   *
   * Conventional offsets:
   *   1 — card / alert (mild lift over the substrate)
   *   2 — dropdown / popover / select / tooltip menu
   *   4 — dialog / sheet / modal
   */
  offset: number;
  /**
   * Override for the shadow level. Defaults to the computed surface level.
   *
   * Pass a fixed value when the component should keep a constant shadow
   * weight regardless of how deeply it's nested — e.g. a dropdown always
   * reads `shadow-surface-3` whether it opens on the page or inside a
   * dialog, even though its background tracks the substrate.
   */
  shadowLevel?: number;
  /**
   * Render onto the child instead of an extra wrapper div. Lets primitives
   * with their own positioned root (Radix Dialog.Content, Popover.Content,
   * etc.) receive the surface bg + shadow + context directly without an
   * intermediate node breaking layout / portaling.
   */
  asChild?: boolean;
  children?: ReactNode;
}

const Elevated = forwardRef<HTMLDivElement, ElevatedProps>(
  ({ offset, shadowLevel, className, children, asChild = false, ...props }, ref) => {
    const substrate = useSurface();
    const level = Math.min(substrate + offset, 8);
    const Comp = asChild ? Slot.Root : "div";
    return (
      <SurfaceProvider value={level}>
        <Comp
          ref={ref}
          className={cn(surfaceClasses(level, shadowLevel ?? level), className)}
          {...props}
        >
          {children}
        </Comp>
      </SurfaceProvider>
    );
  }
);
Elevated.displayName = "Elevated";

export { Elevated };
